import React, { useState } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import { Brain, Sparkles, AlertCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const strategies = [
  { value: 'long_call', label: 'Long Call', description: 'Bullish strategy - Buy call option', outlook: 'Bullish', risk: 'Defined' },
  { value: 'long_put', label: 'Long Put', description: 'Bearish strategy - Buy put option', outlook: 'Bearish', risk: 'Defined' },
  { value: 'short_call', label: 'Short Call (Covered)', description: 'Generate income - Sell call option', outlook: 'Neutral / Bearish', risk: 'Moderate' },
  { value: 'short_put', label: 'Short Put (Cash Secured)', description: 'Generate income - Sell put option', outlook: 'Neutral / Bullish', risk: 'Moderate' },
  { value: 'wheel', label: 'Wheel Strategy', description: 'Sell puts, get assigned, sell calls', outlook: 'Neutral / Bullish', risk: 'Moderate' },
  { value: 'vertical_spread', label: 'Vertical Spread', description: 'Buy and sell options at different strikes', outlook: 'Directional', risk: 'Defined' },
  { value: 'calendar_spread', label: 'Calendar Spread', description: 'Buy and sell options at different expirations', outlook: 'Neutral', risk: 'Defined' },
  { value: 'iron_condor', label: 'Iron Condor', description: 'Neutral strategy - Profit from low volatility', outlook: 'Neutral', risk: 'Defined' },
  { value: 'butterfly', label: 'Butterfly Spread', description: 'Limited risk, limited profit neutral strategy', outlook: 'Neutral', risk: 'Defined' },
  { value: 'straddle', label: 'Straddle', description: 'Profit from high volatility in either direction', outlook: 'High Volatility', risk: 'Defined' },
];

const StrategyAnalyzer = ({ symbol, stockPrice, optionData }) => {
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const { model: selectedModel, setModel: setSelectedModel } = useAIModel();
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const analyzeStrategy = async () => {
    if (!selectedStrategy) {
      toast.error('Please select a strategy');
      return;
    }

    setAnalyzing(true);
    try {
      const legs = optionData ? {
        expiration: optionData.current_expiration,
        calls_sample: optionData.calls?.slice(0, 3),
        puts_sample: optionData.puts?.slice(0, 3)
      } : null;
      const response = await axios.post(`${API}/analyze/strategy`, {
        symbol,
        strategy: selectedStrategy,
        current_price: stockPrice,
        expiration: optionData?.current_expiration || null,
        legs: legs ? [legs] : null,
        ai_model: selectedModel
      });

      setAnalysis(response.data);
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Error analyzing strategy:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to analyze strategy. Please try again.';
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const strategyInfo = strategies.find(s => s.value === selectedStrategy);

  return (
    <div className="space-y-6">
      <Card className="glass-effect border-0 shadow-lg" data-testid="strategy-analyzer-card">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center">
            <Brain className="h-6 w-6 mr-2 text-indigo-600" />
            AI Strategy Analyzer
          </CardTitle>
          <CardDescription>Select a strategy and get AI-powered analysis</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Strategy</label>
              <Select value={selectedStrategy} onValueChange={setSelectedStrategy}>
                <SelectTrigger data-testid="strategy-select">
                  <SelectValue placeholder="Choose a strategy" />
                </SelectTrigger>
                <SelectContent>
                  {strategies.map((strategy) => (
                    <SelectItem key={strategy.value} value={strategy.value} data-testid={`strategy-${strategy.value}`}>
                      <div>
                        <div className="font-semibold">{strategy.label}</div>
                        <div className="text-xs text-gray-500">{strategy.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">AI Model</label>
              <ModelSelector />
            </div>
          </div>

          {strategyInfo && (
            <Alert className="bg-indigo-50 border-indigo-200">
              <AlertCircle className="h-4 w-4 text-indigo-600" />
              <AlertDescription className="text-indigo-900">
                <strong>{strategyInfo.label}:</strong> {strategyInfo.description}
              </AlertDescription>
            </Alert>
          )}

          <Button
            data-testid="analyze-strategy-btn"
            onClick={analyzeStrategy}
            disabled={analyzing || !selectedStrategy}
            className="w-full btn-primary text-lg py-6"
          >
            {analyzing ? (
              <>
                <div className="pulse-animation mr-2">Analyzing...</div>
              </>
            ) : (
              <>
                <Brain className="h-5 w-5 mr-2" />
                Analyze Strategy with AI
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {analysis && (
        <Card className="glass-effect border-0 shadow-lg fade-in" data-testid="analysis-result-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">Analysis Results</CardTitle>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Analyzed by:</span>
                <span className="font-semibold text-indigo-600">
                  {analysis.ai_model === 'gemini' ? 'Gemini 2.5 Pro' : 'Claude 4 Sonnet'}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {(() => {
              const meta = strategies.find(s => s.value === analysis.strategy) || {};
              const hasText = !!(analysis.analysis && String(analysis.analysis).trim());
              return (
            <>
            {/* Key Metrics (factual strategy classification) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <div className="text-sm text-gray-600 mb-1">Outlook</div>
                <div className="text-2xl font-bold text-indigo-600" data-testid="outlook">
                  {meta.outlook || '—'}
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
                <div className="text-sm text-gray-600 mb-1">Risk Profile</div>
                <div className={`text-2xl font-bold ${
                  meta.risk === 'High' ? 'text-red-600' :
                  meta.risk === 'Defined' ? 'text-green-600' :
                  'text-yellow-600'
                }`} data-testid="risk-level">
                  {meta.risk || '—'}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="text-sm text-gray-600 mb-1">Strategy</div>
                <div className="text-xl font-bold text-indigo-600" data-testid="strategy-type">
                  {meta.label || analysis.strategy}
                </div>
              </div>
            </div>

            {/* Detailed Analysis */}
            <div className="bg-white bg-opacity-60 rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-indigo-600" />
                Detailed AI Analysis
              </h3>
              {hasText ? (
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap" data-testid="analysis-text">
                  {analysis.analysis}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800" data-testid="analysis-error">
                  AI explanation is unavailable right now. The strategy classification above is still accurate. Please try again.
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 text-center">
              Analysis generated on {new Date(analysis.timestamp).toLocaleString()}
            </div>
            </>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StrategyAnalyzer;
